import { memo, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../../../../hooks/api.hook";
import SplitBuyPaymentFailed from "./section/splitBuyFailed/splitBuyFailed.section";
import { ComponentLoader } from "../../../../components/loaders";
import SplitBuyPaymentSuccess from "./section/splitBuySuccess/splitBuySuccess.section";
import SplitBuyPaymentPending from "./section/splitBuyPending/splitBuyPending.section";
import { useNavigate } from "react-router";
import { useCart } from "../../../../providers/cart.provider";
import { useAuth } from "../../../../providers/authentication.provider";
import { Mixpanel } from "../../../../utils/mixpanel";

export const OrderCompletePage = memo(() => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [verifiedData, setVerifiedData] = useState(null);
  const navigate = useNavigate();
  const api = useApi();
  const { removeFromCart, removeGuestCartItems } = useCart();
  const { isGuestUser } = useAuth();

  useEffect(() => {
    const verifyTransaction = async () => {
      const reference =
        searchParams.get("trxref") || searchParams.get("reference");

      if (!reference) {
        setStatus("error");
        navigate(-1);
        return;
      }

      try {
        const { data: respData } = await api.get(
          `split-buy/verify-payment?reference=${encodeURIComponent(reference)}`,
          {
            headers: {
              "guest-user-key": isGuestUser
                ? process.env.REACT_APP_API_GUEST_USER_KEY
                : undefined,
            },
          }
        );

        const { data } = respData;
        const payment_status = data?.payment_status || "failed";

        // Get pending checkout data BEFORE removing it
        const pendingCheckout = JSON.parse(
          sessionStorage.getItem("pending_checkout") || "null"
        );

        // Extract all tracking data from pending_checkout
        const splitBuyIds =
          pendingCheckout?.split_buy?.map((item) => item.id) || [];
        const deliveryStates = pendingCheckout?.delivery_state || [];
        const formattedDeliveryStates = Array.isArray(deliveryStates)
          ? [...new Set(deliveryStates)].join("|")
          : deliveryStates || "Not specified";
        const discountApplied = !!pendingCheckout?.discount_code;

        if (payment_status === "success") {
          window.twq("event", "tw-p6cdu-qvqd5", {
            conversion_id: reference,
          });

          setVerifiedData(data);
          setStatus("success");

          Mixpanel.track("Generic-CheckoutCartScreen-PaymentSuccess", {
            split_buy_ids: splitBuyIds.join(","),
            delivery_state: formattedDeliveryStates,
            discount_applied: discountApplied,
          });

          sessionStorage.removeItem("pending_checkout");

          if (isGuestUser) {
            await clearGuestCartItems();
          }
        } else if (payment_status === "pending") {
          setStatus("pending");
        } else {
          Mixpanel.track("Generic-CheckoutCartScreen-PaymentFailed", {
            split_buy_ids: splitBuyIds.join(","),
            delivery_state: formattedDeliveryStates,
            discount_applied: discountApplied,
          });

          setStatus("failed");
        }
      } catch (err) {
        Mixpanel.track("Generic-CheckoutCartScreen-PaymentError", {
          split_buy_ids: "verification_error",
          delivery_state: "error",
          discount_applied: false,
        });

        setStatus("error");
      }
    };

    const clearGuestCartItems = async () => {
      try {
        const pendingCartItems = JSON.parse(
          sessionStorage.getItem("pending_payment_cart_items") || "[]"
        );
        if (!pendingCartItems.length) return;

        removeGuestCartItems(pendingCartItems);
        pendingCartItems.forEach((cartItemId) => removeFromCart(cartItemId));
        sessionStorage.removeItem("pending_payment_cart_items");
      } catch (error) {
        try {
          const pendingCartItems = JSON.parse(
            sessionStorage.getItem("pending_payment_cart_items") || "[]"
          );
          pendingCartItems.forEach((cartItemId) => removeFromCart(cartItemId));
          sessionStorage.removeItem("pending_payment_cart_items");
        } catch {}
      }
    };

    verifyTransaction();
  }, [
    searchParams,
    api,
    navigate,
    removeFromCart,
    removeGuestCartItems,
    isGuestUser,
  ]);

  if (status === "loading") return <ComponentLoader />;
  if (status === "success")
    return <SplitBuyPaymentSuccess verifiedData={verifiedData} />;
  if (status === "pending") return <SplitBuyPaymentPending />;
  return <SplitBuyPaymentFailed />;
});
